'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { ArrayElementType } from '@/integrations/crystallize/types/helpers/ArrayElementType';
import { HeaderFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { usePathname } from 'next/navigation';
import { appendPropertiesTableComponentToUrl, slugify } from '@/utils/url-helper';
import clsx from 'clsx';
import HeaderTextComponent from '../HeaderTextComponent';
import HeaderImageTextComponent from '../HeaderImageTextComponent';
import HeaderLinkListComponent from '../HeaderLinkListComponent';

const DROPDOWN_GRID_COLS_BY_COUNT: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
};

function getDropdownGridColsClass(columnCount: number): string {
  return DROPDOWN_GRID_COLS_BY_COUNT[columnCount] ?? 'grid-cols-3';
}

export default function HeaderDesktopLink({
  link,
}: Readonly<{
  link: ArrayElementType<HeaderFragment['children']>;
}>) {
  const { localizedLink } = useLocaleLink();
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    // Close the dropdown whenever the route changes
    setIsOpen(false);
  }, [pathname]);

  if (link.__typename !== 'Document') {
    return null;
  }

  const externalLink =
    link.directExternalLink?.content?.__typename === 'SingleLineContent'
      ? link.directExternalLink.content.text
      : null;
  const internalLink =
    link.directInternalLink?.content?.__typename === 'ItemRelationsContent'
      ? link.directInternalLink.content.items?.[0]?.path
      : null;

  // If either externalLink or internalLink exists, then hasLink is true
  const hasLink = externalLink || internalLink;

  const wrapperClassName =
    'group flex flex-col justify-between h-full hover:cursor-pointer items-center px-4 text-md duration-200 ease-in hover:bg-neutral-5';

  const linkComponentsContent = link.components?.content;
  const linkComponents =
    linkComponentsContent?.__typename === 'ComponentMultipleChoiceContent'
      ? linkComponentsContent.selectedComponents
      : [];

  const navItemSlug = link.documentName ? slugify(link.documentName) : undefined;
  const trackingPrefix = navItemSlug ? `nav-${navItemSlug}` : undefined;

  const renderableColumnCount = linkComponents.filter(
    (component) =>
      component.content?.__typename === 'PieceContent' &&
      [
        'header-text-component',
        'header-image-text-component',
        'header-link-list-component',
      ].includes(component.id),
  ).length;
  const dropdownGridColsClass = getDropdownGridColsClass(renderableColumnCount);

  return (
    <li
      className={wrapperClassName}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
    >
      {hasLink ? (
        <Link
          href={appendPropertiesTableComponentToUrl(
            hasLink.startsWith('mailto:') ? hasLink : localizedLink(hasLink),
            link.linkParameters,
          )}
          className={wrapperClassName}
          onClick={() => setIsOpen(false)}
          data-tile-element-id={link.documentName ? `nav-${slugify(link.documentName)}` : undefined}
        >
          <div className="h-1" />
          <span className="font-bold">{link.documentName}</span>
          <div className="mx h-1 w-full bg-primary-100 opacity-0 duration-200 ease-in group-hover:opacity-100" />
        </Link>
      ) : (
        <>
          <div className="h-1" />
          <span className="font-bold">{link.documentName}</span>
          <div className="mx h-1 w-full bg-primary-100 opacity-0 duration-200 ease-in group-hover:opacity-100" />
        </>
      )}
      {linkComponents.length > 0 && isOpen && (
        <>
          {/* Div to fill empty space in between navbar and modal */}
          <div className="absolute left-0 top-full h-2 w-full cursor-default" />
          <div
            className={clsx(
              'absolute left-0 top-[105%] z-10 grid w-full cursor-default rounded-md bg-white shadow-nuki-blured [&>*:last-child]:border-r-0 [&>*]:border-r [&>*]:border-neutral-10 [&>*]:px-5 [&>*]:py-8',
              dropdownGridColsClass,
            )}
          >
            {linkComponents.map((component, idx) => {
              if (component.content?.__typename !== 'PieceContent') {
                return null;
              }

              switch (component.id) {
                case 'header-text-component':
                  return (
                    <HeaderTextComponent
                      key={`header-text-component${idx}`}
                      components={component.content.components}
                      setIsOpen={setIsOpen}
                      trackingPrefix={trackingPrefix}
                    />
                  );
                case 'header-image-text-component':
                  return (
                    <HeaderImageTextComponent
                      key={`header-image-text-component${idx}`}
                      components={component.content.components}
                      setIsOpen={setIsOpen}
                      trackingPrefix={trackingPrefix}
                    />
                  );
                case 'header-link-list-component':
                  return (
                    <HeaderLinkListComponent
                      key={`header-link-list-component${idx}`}
                      components={component.content.components}
                      setIsOpen={setIsOpen}
                      trackingPrefix={trackingPrefix}
                    />
                  );
                default:
                  return null;
              }
            })}
          </div>
        </>
      )}
    </li>
  );
}
